import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import CartItem from '../components/cart/CartItem';
import Header from '../components/common/Header';

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartCount, loading, error, fetchCart, clearCart, isAuthenticated } = useCart();

  // Fetch cart when component mounts or when navigating to this page
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.key]); // Fetch on route change and navigation

  const handleClearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your entire cart?')) return;

    try {
      await clearCart();
      alert('Cart cleared successfully');
    } catch (error) {
      console.error('Error clearing cart:', error);
      alert(error.message);
    }
  };

  const handleCheckout = () => {
    if (!cart || cartCount === 0) {
      alert('Your cart is empty');
      return;
    }

    const hasMeds = cart.medicine_items && cart.medicine_items.length > 0;
    const hasServices = cart.service_items && cart.service_items.length > 0;
    if (!hasMeds && !hasServices) {
      alert('No medicines or services in your cart. Use "Book Test Appointment" for medical tests.');
      return;
    }

    // Sync backend medicine/service items to localStorage for OrderStep pages
    const meds = (cart.medicine_items || []).map(item => ({
      id: item.medicine?.id || item.id,
      name: item.medicine?.name || 'Unknown Medicine',
      offer_price: item.medicine?.offer_price || 0,
      qty: item.quantity,
      image: item.medicine?.image || null,
    }));
    localStorage.setItem('cart_meds', JSON.stringify(meds));

    navigate('/order-step-1');
  };

  if (loading && !cart) {
    return (
      <>
        <Header />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading cart...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
          <p>Error: {error}</p>
          <button onClick={fetchCart} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
            Retry
          </button>
        </div>
      </>
    );
  }

  const isEmpty = !cart || cartCount === 0;

  return (
    <>
      <Header />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 style={{ margin: 0 }}>Your Cart ({cartCount} items)</h1>
          {!isEmpty && (
            <button
              onClick={handleClearCart}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '4px',
                background: '#dc3545',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              Clear Cart
            </button>
          )}
        </div>

        {isEmpty ? (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h2>Your cart is empty</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Add items to your cart to get started
            </p>
            <button
              onClick={() => navigate('/Medicines')}
              style={{
                padding: '0.75rem 2rem',
                border: 'none',
                borderRadius: '8px',
                background: '#007bff',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Browse Medicines
            </button>
          </div>
        ) : (
          <>
            {/* Medicine Items */}
            {cart.medicine_items && cart.medicine_items.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ borderBottom: '2px solid #007bff', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                  Medicines
                </h2>
                {cart.medicine_items.map((item) => (
                  <CartItem key={`medicine-${item.id}`} item={item} itemType="medicine" />
                ))}
              </div>
            )}

            {/* Medical Test Items — separate booking flow */}
            {cart.test_items && cart.test_items.length > 0 && (
              <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '2px solid #28a745', borderRadius: '8px', background: '#f0fff4' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ margin: 0, color: '#155724', borderBottom: '2px solid #28a745', paddingBottom: '0.5rem' }}>
                    Medical Tests &amp; Packages
                  </h2>
                  <button
                    onClick={() => {
                      const selectedTests = cart.test_items.map(item => ({
                        id: item.test?.id || item.id,
                        title: item.test?.name || item.test?.title || item.name || item.title,
                        description: item.test?.description || item.description || '',
                        offer_price: item.test?.offer_price || item.offer_price || null,
                        price: item.test?.price || item.price || 0,
                      }));
                      navigate('/medical-tests/booking', { state: { selectedTests } });
                    }}
                    style={{
                      padding: '0.5rem 1.25rem',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#28a745',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                    }}
                  >
                    Book Test Appointment
                  </button>
                </div>
                <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Medical tests require an appointment booking — select a date, time, and address for home sample collection.
                </p>
                {cart.test_items.map((item) => (
                  <CartItem key={`test-${item.id}`} item={item} itemType="test" />
                ))}
              </div>
            )}

            {/* Paramedical Service Items */}
            {cart.service_items && cart.service_items.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ borderBottom: '2px solid #ffc107', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                  Paramedical Services
                </h2>
                {cart.service_items.map((item) => (
                  <CartItem key={`service-${item.id}`} item={item} itemType="service" />
                ))}
              </div>
            )}

            {/* Cart Summary — medicines & services only */}
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              background: '#f8f9fa',
            }}>
              {(() => {
                const hasMedicineOrService =
                  (cart.medicine_items && cart.medicine_items.length > 0) ||
                  (cart.service_items && cart.service_items.length > 0);
                const medicineItemCount =
                  (cart.medicine_items?.length || 0) + (cart.service_items?.length || 0);
                return hasMedicineOrService ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', fontSize: '1.2rem' }}>
                      <span>Total Items:</span>
                      <strong>{medicineItemCount}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', fontSize: '1.5rem', color: '#28a745' }}>
                      <span>Total Amount:</span>
                      <strong>₹{cart.total_amount}</strong>
                    </div>
                    <button
                      onClick={handleCheckout}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#28a745',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      Proceed to Checkout
                    </button>
                  </>
                ) : (
                  <p style={{ textAlign: 'center', color: '#666', margin: 0 }}>
                    Your medicine/service cart is empty. Use the <strong>Book Test Appointment</strong> button above to schedule medical tests.
                  </p>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Cart;
